import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  WarningEventCoverageReason,
  WarningEventRow,
  WarningEventTargetState,
  WarningEventText,
} from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { eventKeys, useWarningEvents } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { TimeRangePicker } from "@/components/controls";
import { Panel } from "@/components/primitives";
import { ErrorState, ForbiddenState, SectionView } from "@/components/SectionState";
import { Breadcrumb, currentRouteCrumb, refPath, routeCrumbAt } from "@/components/drill";
import { clock, dayClock, num, since } from "@/lib/format";
import { PageHeader, useInvalidate } from "@/features/drill/common";
import { useT } from "@/i18n/I18nProvider";

/**
 * Warning Events 화면 — Issue #208
 * --------------------------------------------------------------------------
 * **조회 전용이고 요청은 하나입니다**(ADR 0002). 기존 `core/v1 Event`의
 * `type=Warning` watch 하나를 읽는 서버 투영에서 오고, direct 모드에서만 데이터가
 * 있습니다 — central 배포는 명시적 미지원으로 답하고 이 화면이 그 사실을 말합니다.
 *
 * 규칙 여섯 가지가 이 파일의 모양을 정합니다.
 *
 *  1. **폴링하지 않습니다.** 갱신 주기 선택기를 두지 않습니다 — 고를 수 있는
 *     주기가 있으면 사용자는 그 주기로 갱신된다고 믿게 됩니다. 갱신은 기존 공용
 *     SSE 하나(AppShell의 StreamInvalidator)가 clusterId를 품은 query key를
 *     무효화하는 것과 **수동 갱신**뿐입니다. 이 화면은 SSE 연결을 새로 만들지 않고
 *     `dashboard-stream-message`를 직접 듣지도 않습니다.
 *  2. **다른 질의의 결과를 지금 선택의 결과처럼 보여주지 않습니다.** 이전 값은
 *     cursor만 다른 같은 질의에서만 유지되고(`eventKeys.identity`), 전환 중에는
 *     이어보기도 막습니다.
 *  3. **입력은 어떤 오류에서도 사라지지 않습니다.** 오류는 결과 영역에 그립니다 —
 *     상한 초과로 400을 받은 검색어를 고칠 수 없는 화면이 되면 안 됩니다. 상한은
 *     **UTF-8 바이트**로 미리 검사합니다(한글·이모지는 글자 수와 다릅니다).
 *  4. **사용자 상태를 갱신이 지우지 않습니다.** 검색어·필터·cursor는 URL에 있습니다.
 *  5. **전역 namespace는 URL이 아니라 앱 상태입니다**(#94). `ns` 쿼리를 만들지 않습니다.
 *  6. **"없음"과 "미상"과 "너무 커서 못 보냄"을 구분해 말합니다.** 표시 message와
 *     메타데이터의 상한이 다르므로 잘림 사유도 각자의 숫자를 말합니다.
 */

/** URL에 남는 **이 화면 전용** 상태. 전역 선택(cluster·namespace)은 여기 없습니다. */
const PARAM_Q = "q";
const PARAM_REASON = "reason";
const PARAM_KIND = "kind";
const PARAM_CURSOR = "cursor";
const PARAM_LIMIT = "limit";

/** 화면 전용 파라미터 전부. 대상 화면으로 이동할 때 이 값들을 끌고 가지 않습니다. */
const SCREEN_PARAMS = [PARAM_Q, PARAM_REASON, PARAM_KIND, PARAM_CURSOR, PARAM_LIMIT] as const;

const PAGE_SIZES = [25, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 서버가 거절하는 필터 상한(UTF-8 바이트).
 *
 * 응답이 오기 전에도 알아야 하므로(첫 입력이 바로 상한을 넘을 수 있습니다) 상수로
 * 둡니다. 응답이 오면 서버가 실어 준 `limits.filterBytes`를 씁니다 — 두 값이
 * 어긋나면 **서버가 정본**입니다.
 */
const DEFAULT_FILTER_BYTES = 128;

/** 글자 수가 아니라 **바이트**를 셉니다. 한글은 3바이트, 이모지는 4바이트입니다. */
function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function EventsView() {
  const [params, setParams] = useSearchParams();
  const { t } = useT("events");
  const { clusterId, namespace, range, from, to, patch } = useDashboardParams();

  const urlQuery = params.get(PARAM_Q) ?? "";
  const reason = params.get(PARAM_REASON) ?? "";
  const kind = params.get(PARAM_KIND) ?? "";
  const cursor = params.get(PARAM_CURSOR) ?? "";
  const limit = clampLimit(params.get(PARAM_LIMIT));

  const filters = { clusterId, namespace, range, from, to, q: urlQuery, reason, kind, limit, cursor };
  const query = useWarningEvents(filters);
  const invalidate = useInvalidate(eventKeys.page(filters));

  const limits = query.data?.limits;
  const filterBytes = limits?.filterBytes ?? DEFAULT_FILTER_BYTES;
  const metaBytes = limits?.metaBytes ?? 0;
  const messageBytes = limits?.messageBytes ?? 0;

  /* 입력은 즉시 반영하고 **질의는 디바운스**합니다. 한 글자마다 요청을 보내면
     서버 투영을 훑는 비용이 타이핑 속도로 쌓입니다. */
  const [draft, setDraft] = useState(urlQuery);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  /** 상한을 넘은 입력은 **URL에 넣지 않습니다** — 서버가 거절할 질의를 보내지 않습니다. */
  const draftTooLong = utf8Bytes(draft.trim()) > filterBytes;

  /**
   * 화면 로컬 쿼리를 **한 군데서만** 고칩니다.
   *
   * 검색 디바운스·정확 일치 필터·cursor 이동·필터 해제가 모두 "이전 쿼리를 복사해
   * 고치고 history를 대체한다"는 같은 모양입니다. 네 곳에 따로 두면 그중 하나가
   * `{ replace: true }`를 빼먹는 순간 뒤로가기가 필터 변경 하나하나를 되짚습니다.
   */
  const mutateParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setParam = useCallback(
    (name: string, value: string) => {
      mutateParams((next) => {
        if (value) next.set(name, value);
        else next.delete(name);
        /* 필터가 바뀌면 cursor는 무의미합니다. */
        if (name !== PARAM_CURSOR) next.delete(PARAM_CURSOR);
      });
    },
    [mutateParams],
  );

  useEffect(() => {
    if (draft === urlQuery || draftTooLong) return;
    /* 검색어가 바뀌면 **cursor를 버립니다** — 다른 질의의 이어보기 지점은
       서버가 거절하고, 거절되지 않더라도 의미가 없습니다. `setParam`이
       `PARAM_CURSOR`가 아닌 모든 변경에서 cursor를 지웁니다. */
    const timer = window.setTimeout(() => setParam(PARAM_Q, draftRef.current), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, urlQuery, draftTooLong, setParam]);

  /* URL이 밖에서 바뀌면(뒤로가기·공유 링크) 입력도 따라갑니다. */
  useEffect(() => {
    setDraft((current) => (current === urlQuery ? current : urlQuery));
  }, [urlQuery]);

  /** 정확 일치 필터의 입력값. 상한 초과를 **입력에 남겨 두고** URL에는 넣지 않습니다. */
  const [reasonDraft, setReasonDraft] = useState(reason);
  const [kindDraft, setKindDraft] = useState(kind);
  useEffect(() => setReasonDraft((v) => (v === reason ? v : reason)), [reason]);
  useEffect(() => setKindDraft((v) => (v === kind ? v : kind)), [kind]);
  const reasonTooLong = utf8Bytes(reasonDraft.trim()) > filterBytes;
  const kindTooLong = utf8Bytes(kindDraft.trim()) > filterBytes;

  const commitExact = useCallback(
    (name: string, value: string) => {
      if (utf8Bytes(value.trim()) > filterBytes) return;
      setParam(name, value.trim());
    },
    [filterBytes, setParam],
  );

  /**
   * 이어보기 지점이 더 이상 유효하지 않다는 **지속되는** 안내.
   *
   * cursor를 지우면 다음 렌더에서 오류가 사라지므로, 오류만 보고 그리면 안내가 한
   * 프레임만 보입니다. 만료(5분)와 "다른 프로세스가 발급한 cursor는 거절된다"는
   * 제약이 사용자에게 관찰되어야 하므로 상태로 들고 있다가, **의미가 바뀌는 다음
   * 사용자 동작**에서만 지웁니다.
   */
  const [cursorRecovered, setCursorRecovered] = useState(false);
  const invalidCursor = query.error instanceof HttpError && query.error.body.code === "invalid_cursor";
  useEffect(() => {
    if (!invalidCursor) return;
    setCursorRecovered(true);
    if (cursor) setParam(PARAM_CURSOR, "");
  }, [invalidCursor, cursor, setParam]);

  const unsupported = query.error instanceof HttpError && query.error.body.code === "central_mode_unsupported";
  const forbidden = query.error instanceof HttpError && query.error.status === 403;
  /* 다른 오류(400·5xx)는 결과 영역에 그립니다 — 입력을 DOM에서 없애지 않습니다. */
  const generalError = query.error && !unsupported && !forbidden && !invalidCursor ? query.error : undefined;

  /* 대상 링크가 끌고 갈 질의는 **시간 범위뿐**입니다. 이 화면의 검색어·필터·cursor를
     상세 화면 주소에 쌓아 두면 의미 없는 파라미터가 북마크에 남습니다. */
  const targetSearch = useMemo(() => {
    const carried = new URLSearchParams(params);
    for (const name of SCREEN_PARAMS) carried.delete(name);
    return carried.toString();
  }, [params]);

  /**
   * **전환 중에는 이어보기를 막습니다.**
   *
   * 요청이 도는 동안의 `page`는 (신원이 같을 때만 유지되는) 이전 페이지의 것입니다.
   * 그 상태에서 "다음"을 누르면 이미 불러오고 있는 cursor를 다시 보냅니다.
   */
  const transitioning = query.isFetching || query.isPlaceholderData;
  const failed = Boolean(generalError) || forbidden;
  const section = failed ? undefined : query.data?.events;
  const syncing = section?.reason === "projection_syncing";
  const page = failed ? undefined : query.data?.page;
  const referenceIso = query.data?.generatedAt;
  const filtered = Boolean(urlQuery || reason || kind || draft || reasonDraft || kindDraft);

  return (
    <div className="page">
      <PageHeader
        crumbs={<Breadcrumb items={[routeCrumbAt("overview", "/", targetSearch), currentRouteCrumb("events")]} />}
        title={t("events.title")}
        subtitle={t("events.subtitle")}
        controls={
          /* **갱신 주기 선택기가 없습니다.** 범위 선택과 수동 갱신만 둡니다 —
             고를 수 있는 주기가 있으면 사용자는 그 주기로 갱신된다고 믿습니다. */
          <div className="row row--wrap">
            <TimeRangePicker
              range={range}
              from={from}
              to={to}
              onChange={(next) => patch({ range: next })}
              onCustom={(nextFrom, nextTo) => patch({ range: "custom", from: nextFrom, to: nextTo })}
            />
            <div className="refresh">
              <button type="button" className="linkish" onClick={invalidate} aria-live="off">
                {t("events.refresh.now")}
              </button>
              <span className="refresh__status" aria-live="polite">
                {query.isFetching
                  ? t("events.refresh.busy")
                  : query.dataUpdatedAt
                    ? t("events.refresh.updated", { time: clock(query.dataUpdatedAt) })
                    : ""}
              </span>
            </div>
          </div>
        }
      />

      <div className="chips" role="group" aria-label={t("events.filter.label")}>
        <label className="field">
          <span className="field__label">{t("events.filter.q.label")}</span>
          <input
            type="search"
            value={draft}
            placeholder={t("events.filter.q.placeholder")}
            aria-describedby="events-q-hint"
            aria-invalid={draftTooLong || undefined}
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t("events.filter.reason.label")}</span>
          <input
            type="text"
            value={reasonDraft}
            placeholder={t("events.filter.reason.placeholder")}
            aria-invalid={reasonTooLong || undefined}
            onChange={(e) => {
              setReasonDraft(e.target.value);
              commitExact(PARAM_REASON, e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span className="field__label">{t("events.filter.kind.label")}</span>
          <input
            type="text"
            value={kindDraft}
            placeholder={t("events.filter.kind.placeholder")}
            aria-invalid={kindTooLong || undefined}
            onChange={(e) => {
              setKindDraft(e.target.value);
              commitExact(PARAM_KIND, e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span className="field__label">{t("events.filter.limit.label")}</span>
          <select
            value={String(limit)}
            onChange={(e) => {
              setCursorRecovered(false);
              setParam(PARAM_LIMIT, e.target.value);
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={String(size)}>
                {num(size)}
              </option>
            ))}
          </select>
        </label>
        {filtered && (
          <button
            type="button"
            className="linkish"
            onClick={() => {
              setDraft("");
              setReasonDraft("");
              setKindDraft("");
              setCursorRecovered(false);
              mutateParams((next) => {
                for (const name of SCREEN_PARAMS) next.delete(name);
              });
            }}
          >
            {t("events.filter.clear")}
          </button>
        )}
      </div>
      <p className="muted" id="events-q-hint">
        {t("events.filter.q.hint")} {t("events.refresh.note")}
      </p>
      {(draftTooLong || reasonTooLong || kindTooLong) && (
        <p className="notice" role="alert">
          {t("events.filter.tooLong", { bytes: num(filterBytes) })}
        </p>
      )}

      {cursorRecovered && (
        <p className="notice" role="status">
          {t("events.cursor.invalid")}
        </p>
      )}

      {unsupported ? (
        <section className="panel">
          <div className="panel__body">
            <div className="state state--degraded">
              <span className="state__glyph" aria-hidden="true">
                ▲
              </span>
              <span className="state__title">{t("events.unsupported.title")}</span>
              <span className="state__detail">{t("events.unsupported.detail")}</span>
            </div>
          </div>
        </section>
      ) : (
        <Panel
          title={t("events.panel.title")}
          subtitle={t("events.panel.subtitle", { limit: num(limit) })}
          section={section}
          referenceIso={referenceIso}
          /* `projection_syncing`은 상류 장애가 아니라 **기동 중**입니다 —
             `일부 장애` 배지를 붙이면 운영자가 멀쩡한 클러스터를 의심합니다. */
          outage={!syncing}
          flush
        >
          {forbidden ? (
            <div className="panel__body">
              <ForbiddenState detail={(query.error as HttpError).body.message} />
            </div>
          ) : generalError ? (
            <div className="panel__body">
              <ErrorState
                detail={generalError instanceof HttpError ? generalError.body.message : (generalError as Error).message}
                onRetry={invalidate}
              />
            </div>
          ) : syncing ? (
            <div className="panel__body">
              <div className="state state--degraded">
                <span className="state__glyph" aria-hidden="true">
                  ▲
                </span>
                <span className="state__title">{t("events.syncing.title")}</span>
                <span className="state__detail">{t("events.syncing.detail")}</span>
              </div>
            </div>
          ) : (
            <SectionView
              section={section}
              loading={query.isLoading}
              emptyTitle={t("events.empty.title")}
              emptyDetail={t("events.empty.detail")}
            >
              {(rows) => (
                <EventTable
                  rows={rows}
                  referenceIso={referenceIso}
                  messageBytes={messageBytes}
                  metaBytes={metaBytes}
                  search={targetSearch}
                />
              )}
            </SectionView>
          )}
        </Panel>
      )}

      {page && (
        <div className="row row--wrap" role="group" aria-label={t("events.panel.title")}>
          <span className="muted">{t("events.page.position", { returned: num(page.returned) })}</span>
          <span
            className={page.coverage === "exact" ? "muted" : "chip"}
            title={page.coverage === "exact" ? t("events.coverage.exactTitle") : undefined}
          >
            {page.coverage === "exact" ? t("events.coverage.exact") : t("events.coverage.at_least")}
          </span>
          {page.coverageReason && (
            <span className="muted">
              {t(COVERAGE_REASON[page.coverageReason], { rows: num(limits?.projectionRows ?? 0) })}
            </span>
          )}
          {page.truncated && <span className="muted">{t("events.page.truncated")}</span>}
          {cursor && (
            <button
              type="button"
              className="linkish"
              disabled={transitioning}
              onClick={() => {
                setCursorRecovered(false);
                setParam(PARAM_CURSOR, "");
              }}
            >
              {t("events.page.first")}
            </button>
          )}
          {page.hasMore && page.nextCursor && (
            <button
              type="button"
              className="linkish"
              /* 전환 중에는 막습니다 — 이미 불러오고 있는 cursor를 다시 보내지 않습니다. */
              disabled={transitioning}
              onClick={() => setParam(PARAM_CURSOR, page.nextCursor ?? "")}
            >
              {t("events.page.next")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 행 표. **native `<table>`**이고 한 페이지 행 수는 서버가 100으로 막습니다 —
 * 가상 스크롤이 필요한 크기가 아니고, native 표가 키보드 순서와 스크린리더
 * 읽기를 그대로 줍니다. 스크롤은 기존 토큰화된 `panel__scroll`이 담당합니다.
 */
function EventTable({
  rows,
  referenceIso,
  messageBytes,
  metaBytes,
  search,
}: {
  rows: WarningEventRow[];
  referenceIso: string | undefined;
  messageBytes: number;
  metaBytes: number;
  search: string;
}) {
  const { t } = useT("events");
  return (
    <div className="panel__scroll">
      <table className="ds-data-table ds-data-table--compact">
        <thead>
          <tr>
            <th scope="col">{t("events.column.lastSeen")}</th>
            <th scope="col">{t("events.column.namespace")}</th>
            <th scope="col">{t("events.column.reason")}</th>
            <th scope="col">{t("events.column.source")}</th>
            <th scope="col">{t("events.column.message")}</th>
            <th scope="col">{t("events.column.target")}</th>
            <th scope="col">{t("events.column.count")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            /* key는 Event UID입니다 — 이름은 재사용되지만 UID는 아닙니다. */
            <tr key={row.id}>
              <td>
                <LastSeenCell row={row} referenceIso={referenceIso} />
              </td>
              <td className="ds-ident">{row.namespace || <Absent />}</td>
              <td>
                <TextCell text={row.reason} limitBytes={metaBytes} />
              </td>
              <td>
                <TextCell text={row.source} limitBytes={metaBytes} />
              </td>
              <td>
                <TextCell text={row.message} limitBytes={messageBytes} />
              </td>
              <td>
                <TargetCell row={row} search={search} />
              </td>
              <td className="num">
                {row.countUnavailable || row.count === undefined ? (
                  <span className="muted">{t("events.count.unavailable")}</span>
                ) : (
                  num(row.count)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Absent() {
  const { t } = useT("events");
  return <span className="muted">{t("events.text.absent")}</span>;
}

/**
 * 문자열 한 칸. 네 상태를 **다르게** 말합니다.
 *
 *  · `absent`             — 원천에 값이 없었습니다.
 *  · `withheld_too_large` — 서버가 **필드 전체를 내려보내지 않았습니다.**
 *  · `withheld_processing_limit` — 마스킹 후보 처리 상한을 넘어 **필드 전체를
 *    내려보내지 않았습니다.** raw 바이트 상한과 다른 사실입니다.
 *    원문 조회·복사 경로를 만들지 않습니다 — 마스킹이 서버에서만 일어나는 이유입니다.
 *    문구는 필드 이름을 말하지 않습니다 — 이 칸은 message뿐 아니라 reason·source에도
 *    쓰이므로 "Message가 보류됨"이라고 쓰면 메타데이터 칸에서 거짓이 됩니다.
 *  · `ok` + truncated    — 보이는 것이 전부가 아니라는 사실을 함께 표시합니다.
 *
 * `limitBytes`는 **그 필드의** 상한입니다. message는 1,024, source·reason은 128이며
 * 한 값을 돌려 쓰면 잘림 사유가 틀린 숫자를 말합니다.
 */
function TextCell({ text, limitBytes }: { text: WarningEventText; limitBytes: number }) {
  const { t } = useT("events");
  if (text.state === "absent") return <Absent />;
  if (text.state === "withheld_too_large") {
    return <span className="muted">{t("events.text.withheld")}</span>;
  }
  if (text.state === "withheld_processing_limit") {
    return <span className="muted">{t("events.text.withheldProcessingLimit")}</span>;
  }
  return (
    <span className="events__message">
      {text.value}
      {text.truncated && (
        <span className="chip" title={t("events.text.truncatedTitle", { bytes: num(limitBytes) })}>
          {t("events.text.truncated")}
        </span>
      )}
    </span>
  );
}

function LastSeenCell({ row, referenceIso }: { row: WarningEventRow; referenceIso: string | undefined }) {
  const { t } = useT("events");
  if (row.lastSeenUnavailable || !row.lastSeen) {
    return <span className="muted">{t("events.time.unavailable")}</span>;
  }
  const ms = Date.parse(row.lastSeen);
  const absolute = Number.isFinite(ms) ? dayClock(ms) : row.lastSeen;
  return (
    <span className="row" title={absolute}>
      <span>{referenceIso ? since(row.lastSeen, referenceIso) : absolute}</span>
      {row.lastSeenFirstObservedOnly && (
        <span className="chip" title={t("events.time.firstObservedTitle")}>
          {t("events.time.firstObserved")}
        </span>
      )}
    </span>
  );
}

/**
 * 대상 칸.
 *
 * **링크는 `linkable`일 때만 만듭니다.** 그 상태는 서버가 "목적지가 요청한 UID를
 * 실제로 강제한다"고 보장한 경우이고, 그래서 링크에 `uid`를 **명시적으로** 싣습니다.
 * 공용 링크 helper가 자동으로 싣지 않는 이유는 기존 Overview·Topology·Alerts
 * 링크까지 UID 묶음 요청이 되면 central 모드의 기존 탐색이 깨지기 때문입니다.
 *
 * 그 밖의 상태는 **닫힌 사유**를 문장으로 말합니다. 빈 칸이나 죽은 링크를 만들지 않고,
 * 가려야 하는 신원(권한 밖·어긋난 참조)은 이름조차 서버가 내려보내지 않습니다.
 */
function TargetCell({ row, search }: { row: WarningEventRow; search: string }) {
  const { t } = useT("events");
  const target = row.target;
  const reason = TARGET_REASON[target.state];
  if (target.state !== "linkable" || !target.name) {
    return (
      <span className="row">
        {target.kind && <span className="muted">{target.kind}</span>}
        {target.name && <span className="ds-ident">{target.name}</span>}
        <span className="muted">{t(reason)}</span>
      </span>
    );
  }
  const workloadKind = asWorkloadKind(target.kind);
  const to =
    target.kind === "Pod"
      ? refPath({ namespace: target.namespace, podName: target.name, podUid: target.uid }, search)
      : workloadKind
        ? refPath(
            { namespace: target.namespace, workloadKind, workloadName: target.name },
            search,
            /* **이 화면만** `uid`를 명시적으로 싣습니다. 공용 helper가 자동으로 싣게
               되면 기존 Overview·Topology·Alerts 링크까지 UID 묶음 요청이 되어
               central 모드의 기존 탐색이 깨집니다. */
            target.uid ? { uid: target.uid } : {},
          )
        : "";
  if (!to) {
    /* 서버가 linkable이라 했지만 이 빌드가 아는 목적지 kind가 아닙니다.
       죽은 링크를 만들지 않고 텍스트로 남깁니다 — 계약이 넓어지면 여기가 먼저 보입니다. */
    return (
      <span className="row">
        <span className="muted">{target.kind}</span>
        <span className="ds-ident">{target.name}</span>
        <span className="muted">{t("events.target.kind_unsupported")}</span>
      </span>
    );
  }
  return (
    <span className="row">
      <span className="muted">{target.kind}</span>
      <Link to={to} title={t("events.target.linkable")} className="ds-ident">
        {target.name}
      </Link>
    </span>
  );
}

/**
 * 커버리지 사유 → 메시지 ID. **닫힌 표**입니다.
 *
 * 템플릿 문자열로 ID를 만들면 i18n 게이트가 그 ID를 "쓰이지 않는다"고 읽고,
 * 실제로 서버가 보내는 사유의 문구가 카탈로그에서 조용히 사라질 수 있습니다.
 */
const COVERAGE_REASON: Record<WarningEventCoverageReason, string> = {
  retention_evicted: "events.coverage.retention_evicted",
  search_text_unavailable: "events.coverage.search_text_unavailable",
  cursor_anchor_evicted: "events.coverage.cursor_anchor_evicted",
};

/** 닫힌 사유 → 메시지 ID. 서버 열거값이 늘면 여기서 타입이 깨집니다. */
const TARGET_REASON: Record<WarningEventTargetState, string> = {
  linkable: "events.target.linkable",
  uid_unknown: "events.target.uid_unknown",
  identity_invalid: "events.target.identity_invalid",
  kind_unsupported: "events.target.kind_unsupported",
  cluster_scoped: "events.target.cluster_scoped",
  namespace_mismatch: "events.target.namespace_mismatch",
  out_of_scope: "events.target.out_of_scope",
};

/**
 * 목적지가 있는 워크로드 kind. 그 밖은 링크를 만들지 않습니다.
 *
 * **서버의 `warningEventLinkableKinds`·`WorkloadIdentity`가 지원하는 집합과 정확히
 * 같아야 합니다.** `ReplicaSet`은 서버가 신원을 조회하지 않으므로 여기에 없습니다 —
 * 넣어 두면 서버가 나중에 `linkable`을 넓혔을 때 **신원 검증 없는 경로가 조용히**
 * 링크가 됩니다. 아래 `TargetCell`은 이 표에 없는 kind를 링크로 만들지 않고
 * `kind_unsupported` 텍스트로 남깁니다.
 */
const WORKLOAD_KINDS = ["Deployment", "StatefulSet", "DaemonSet", "CronJob"] as const;
type WorkloadKind = (typeof WORKLOAD_KINDS)[number];

function asWorkloadKind(kind: string | undefined): WorkloadKind | undefined {
  return WORKLOAD_KINDS.includes(kind as WorkloadKind) ? (kind as WorkloadKind) : undefined;
}

/** 서버가 막는 상한 안으로 접습니다. 밖의 값은 기본값으로 떨어집니다. */
export function clampLimit(raw: string | null): number {
  const parsed = Number(raw);
  return PAGE_SIZES.includes(parsed as (typeof PAGE_SIZES)[number]) ? parsed : 50;
}
